import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Paper, Typography, Stack, TextField, FormControl, InputLabel, Select,
  MenuItem, Button, Alert, CircularProgress, Box,
  Tabs, Tab, Divider,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import ExternalStorageCard from '../../components/ExternalStorageCard';
import ExternalStorageDialog from '../../components/ExternalStorageDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import ProjectOwnerSelect from '../../components/ProjectOwnerSelect';

// Focused, single-purpose page used by the admin Control Center
// Projects pane. Renders only the project form (create or edit) so a
// floating window opened from the pane shows the form by itself,
// without the surrounding My Projects dashboard chrome. Mirrors the
// legacy edit-project dialog by exposing two tabs:
//   - Basic Info       (always visible)
//   - External Storage (edit mode only — needs a project id)
//
// On save:
//   - Posts a window message {type: 'projectChanged'} so the parent
//     Control Center pane refreshes its row.
//   - Asks the parent to close the floating window.
const PROJECT_TYPES = [
  { value: 'research', label: 'Research' },
  { value: 'education', label: 'Education' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'personal', label: 'Personal' },
  { value: 'testing', label: 'Testing' },
];

const PROJECT_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const emptyForm = {
  name: '',
  code: '',
  description: '',
  type: 'research',
  status: 'active',
  // No owner preselected on create (#1177): the form used to default to Personal, which is how
  // ambiguous ownership got recorded without anyone choosing it. ProjectOwnerSelect fills this in
  // automatically when the user has exactly one eligible owner.
  owner_type: null,
  owner_id: null,
};

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const ProjectFormStandalone = ({ mode = 'create' }) => {
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = mode === 'edit' && !!id;

  const [tab, setTab] = useState(0);
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [project, setProject] = useState(null); // edit mode only
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  // External Storage tab state — same shape as the legacy dialog.
  const [storageConfigs, setStorageConfigs] = useState([]);
  const [storageError, setStorageError] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageDialogOpen, setStorageDialogOpen] = useState(false);
  const [editingStorageConfig, setEditingStorageConfig] = useState(null);
  const [testingStorageConfigId, setTestingStorageConfigId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Org storage destinations this project can derive from (#1021).
  const [orgStorageProviders, setOrgStorageProviders] = useState([]);
  useEffect(() => {
    apiService.getOrgStorageProviders()
      .then(setOrgStorageProviders)
      .catch(() => setOrgStorageProviders([]));
  }, []);

  const loadStorageConfigs = async (projectId) => {
    if (!projectId) return;
    try {
      const r = await apiService.getProjectStorageConfigs(projectId);
      if (r?.success) setStorageConfigs(r.data?.configs || []);
    } catch (e) {
      console.error('Failed to load project storage configs:', e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Eligible owners (#1177) — Personal + every org the user belongs to, each carrying the
        // subscription that backs it. Replaces the raw organization list, which couldn't say
        // whether an owner was payable and silently defaulted to Personal.
        const ownerRes = await apiService.getEligibleProjectOwners().catch(() => null);
        if (!cancelled) {
          const list = ownerRes?.data?.owners || [];
          setOwners(Array.isArray(list) ? list : []);
          setOwnersLoading(false);
        }
      } catch (_) { /* */ }

      if (isEdit) {
        try {
          const r = await apiService.getProject(id);
          if (cancelled) return;
          const p = r?.data?.project || r?.data || r || {};
          setProject(p);
          setForm({
            name: p.name || '',
            code: p.code || '',
            description: p.description || '',
            type: p.type || 'research',
            status: p.status || 'active',
            owner_type: p.owner_type || 'user',
            owner_id: p.owner_type === 'organization' ? (p.owner_id?._id || p.owner_id || null) : null,
          });
          await loadStorageConfigs(id);
        } catch (e) {
          if (!cancelled) setError(e?.response?.data?.error?.message || e?.message || 'Failed to load project');
        } finally {
          if (!cancelled) setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const isAdmin = user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';
  const canManageStorage = !!project && (
    isAdmin || project.is_owner || project.user_permissions?.can_manage_storage
  );

  // An owner must be settled before create — either auto-selected (single eligible owner) or picked.
  const isValid = form.name.trim() && form.code.trim() && form.description.trim()
    && (isEdit || !!form.owner_type);

  const closeWindow = () => {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'projectChanged' }, '*');
        window.parent.postMessage({ type: 'closeFloatingWindow' }, '*');
      } else {
        window.close();
      }
    } catch (_) { /* */ }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = { ...form };
      if (payload.owner_type === 'user' || !payload.owner_type) {
        // Personal: the server derives owner_id from the authenticated user.
        delete payload.owner_id;
        payload.owner_type = 'user';
      }
      let projectId = id;
      if (isEdit) {
        await apiService.updateProject(id, payload);
      } else {
        const created = await apiService.createProject(payload);
        projectId = created?.data?.project?._id || created?.data?._id || created?._id || projectId;
      }
      // Persist any draft storage configs against the (possibly just-
      // created) project. Edit mode storage configs already round-
      // trip through the API on add/edit, so this loop is essentially
      // a no-op there. Failures don't roll back the project save —
      // we surface the message and keep the user in the form so they
      // can retry.
      const drafts = storageConfigs.filter((c) => c._draft);
      const failedDrafts = [];
      for (const draft of drafts) {
        try {
          const { _draft: _draftFlag, _id: _draftId, ...payloadDraft } = draft;
          void _draftFlag; void _draftId;
          await apiService.createExternalStorageConfig({
            ...payloadDraft,
            owner_type: 'project',
            owner_id: projectId,
          });
        } catch (e) {
          failedDrafts.push({ name: draft.display_name || draft.name || '(unnamed)', err: e?.response?.data?.error?.message || e?.message || 'Save failed' });
        }
      }
      if (failedDrafts.length > 0) {
        setError(`Project saved, but ${failedDrafts.length} storage configuration(s) failed: ${failedDrafts.map((f) => `${f.name} — ${f.err}`).join('; ')}`);
        // Don't close the window so the user can retry the storage tab.
        return;
      }
      setDone(true);
      closeWindow();
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.message || (isEdit ? 'Failed to update project' : 'Failed to create project'));
    } finally {
      setSaving(false);
    }
  };

  const handleStorageSave = async (data, configId) => {
    setStorageLoading(true);
    setStorageError(null);
    try {
      // Edit mode (project already exists) — round-trip via API.
      if (project?._id) {
        const payload = { ...data, owner_type: 'project', owner_id: project._id };
        if (configId) {
          await apiService.updateExternalStorageConfig(configId, payload);
        } else {
          await apiService.createExternalStorageConfig(payload);
        }
        await loadStorageConfigs(project._id);
      } else {
        // Create mode — stash as a draft. Drafts get persisted on
        // the main Save once the project itself exists.
        setStorageConfigs((prev) => {
          if (configId) {
            return prev.map((c) => (c._id === configId ? { ...c, ...data, _draft: true, _id: configId } : c));
          }
          return [...prev, { ...data, _draft: true, _id: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` }];
        });
      }
      setStorageDialogOpen(false);
      setEditingStorageConfig(null);
    } catch (err) {
      setStorageError(err.response?.data?.error?.message || err.message || 'Failed to save storage configuration');
    } finally {
      setStorageLoading(false);
    }
  };

  const handleStorageDelete = (config) => {
    setConfirm({
      title: 'Delete Storage Configuration',
      message: (
        <span>Delete storage configuration <strong>{config.display_name}</strong>?</span>
      ),
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          // Drafts are local-only; just drop them from state.
          if (config._draft) {
            setStorageConfigs((prev) => prev.filter((c) => c._id !== config._id));
            return;
          }
          await apiService.deleteExternalStorageConfig(config._id);
          setStorageConfigs((prev) => prev.filter((c) => c._id !== config._id));
        } catch (err) {
          console.error('Failed to delete storage configuration:', err);
          throw err;
        }
      },
    });
  };

  const handleStorageTest = async (configId) => {
    // Drafts have no server id yet — skip.
    const cfg = storageConfigs.find((c) => c._id === configId);
    if (cfg?._draft) return;
    setTestingStorageConfigId(configId);
    setError(null);
    try {
      const res = await apiService.testExternalStorageConfig(configId);
      // The endpoint returns 200 {success:true} on success and throws on
      // failure; guard the in-body flag too.
      if (res && res.success === false) {
        setError(res.error?.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message || 'Connection test failed');
    } finally {
      // Always reload so the card reflects the persisted last-test result,
      // whether the test passed or failed (previously only reloaded on success,
      // so a failed test looked like nothing happened).
      if (project) await loadStorageConfigs(project._id).catch(() => {});
      setTestingStorageConfigId(null);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 3 }}>
      {/* No page-level heading — the floating window chrome already
          shows "Edit Project" / "Create Project" in the title bar. */}
      <Paper sx={{ p: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Basic Info" />
          {/* External Storage is available in both create and edit
              mode. In create mode each entry is a local draft until
              the user clicks Save — at that point the project is
              created first, then each draft is POSTed against the
              new project id. */}
          <Tab label="External Storage" icon={<CloudUploadIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {done && <Alert severity="success">Saved.</Alert>}
              <TextField
                label="Project Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                fullWidth required
                autoFocus={!isEdit}
              />
              <TextField
                label="Project Code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                fullWidth required
                helperText="Short unique identifier (e.g. PROJ-001)"
              />
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                multiline rows={3} fullWidth required
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  label="Type"
                >
                  {PROJECT_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isEdit && (
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    label="Status"
                  >
                    {PROJECT_STATUSES.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <ProjectOwnerSelect
                owners={owners}
                loading={ownersLoading}
                isEdit={isEdit}
                value={{ owner_type: form.owner_type, owner_id: form.owner_id }}
                onChange={({ owner_type, owner_id }) => setForm({ ...form, owner_type, owner_id })}
                disabled={saving}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button onClick={closeWindow} disabled={saving}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isValid || saving}
                startIcon={saving ? <CircularProgress size={16} /> : null}
              >
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </Stack>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">
                Storage destinations for this project
              </Typography>
              {/* In edit mode we gate Add behind the project's
                  user_permissions; in create mode the current user
                  is implicitly the owner-to-be, so always allow. */}
              {(isEdit ? canManageStorage : true) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setEditingStorageConfig(null); setStorageError(null); setStorageDialogOpen(true); }}
                >
                  Add Storage
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {!isEdit && storageConfigs.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                These destinations will be saved with the project when you click <strong>Create</strong>.
              </Alert>
            )}
            {storageConfigs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {isEdit
                  ? 'No external storage destinations configured for this project. Add one to automatically copy FITS files to external storage.'
                  : 'No external storage destinations yet. Add one and it will be created with the project.'}
              </Typography>
            ) : (
              storageConfigs.map((config) => (
                <ExternalStorageCard
                  key={config._id}
                  config={config}
                  onEdit={(isEdit ? canManageStorage : true) ? (c) => { setEditingStorageConfig(c); setStorageError(null); setStorageDialogOpen(true); } : null}
                  onDelete={(isEdit ? canManageStorage : true) ? handleStorageDelete : null}
                  // Test only meaningful for persisted (non-draft) configs.
                  onTest={(isEdit && canManageStorage && !config._draft) ? handleStorageTest : null}
                  testing={testingStorageConfigId === config._id}
                />
              ))
            )}
            {(isEdit ? canManageStorage : true) && (
              <ExternalStorageDialog
                open={storageDialogOpen}
                onClose={() => { setStorageDialogOpen(false); setEditingStorageConfig(null); setStorageError(null); }}
                onSave={handleStorageSave}
                config={editingStorageConfig}
                loading={storageLoading}
                error={storageError}
                orgProviders={orgStorageProviders}
              />
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
              {!isEdit ? (
                <>
                  <Button onClick={closeWindow} disabled={saving}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!isValid || saving}
                    startIcon={saving ? <CircularProgress size={16} /> : null}
                  >
                    Create
                  </Button>
                </>
              ) : (
                <Button onClick={closeWindow}>Done</Button>
              )}
            </Stack>
          </Box>
        </TabPanel>
      </Paper>
      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Container>
  );
};

export default ProjectFormStandalone;
